import React, { Suspense, lazy, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
// HomePage stays eager — it's the landing / LCP route, so we don't want a
// chunk round-trip before first paint. Everything else is lazy-loaded below
// so heavy, page-specific deps (chatscope for chat, react-slick +
// react-markdown for post detail, the add-post/profile form code) stay OUT of
// the initial marketplace bundle and only fetch when the user navigates there.
import HomePage from "./containers/HomePage";
import FullPageLoading from "./components/FullPageLoading/FullPageLoading";
import { openAuthWizard } from "./utilities/authWizard";

// /blog is an SEO + traffic-driver surface that doesn't load on the
// marketplace's hot path. Lazy-importing it keeps react-markdown + the blog
// manifest entirely out of the initial bundle — they only fetch when the
// user navigates into the section.
const BlogListPage = lazy(() => import("./containers/BlogPage/BlogListPage"));
const BlogPostPage = lazy(() => import("./containers/BlogPage/BlogPostPage"));

// Marketplace routes — split out of the initial bundle. Default-export pages
// import directly; the named-export ones map `.then` to a { default } shape
// so React.lazy accepts them.
const AddPostPage = lazy(() => import("./containers/AddPostPage"));
const ProfilePageEdit = lazy(() => import("./containers/ProfilePageEdit"));
const MyPostsPage = lazy(() => import("./containers/MyPostsPage/MyPostsPage"));
const PostDetailPage = lazy(() =>
  import("./containers/PostDetailPage/PostDetailPage"),
);
const PrivacyPage = lazy(() => import("./containers/PrivacyPage/PrivacyPage"));
const SettingsPage = lazy(() => import("./containers/SettingsPage/SettingsPage"));
const TermsPage = lazy(() => import("./containers/TermsPage/TermsPage"));
const ProfilePage = lazy(() =>
  import("./containers/ProfilePage/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const OtherProfilePage = lazy(() =>
  import("./containers/ProfilePage/OtherProfilePage").then((m) => ({
    default: m.OtherProfilePage,
  })),
);
const OthersPostsPage = lazy(() =>
  import("./containers/MyPostsPage/OthersPostPage").then((m) => ({
    default: m.OthersPostsPage,
  })),
);
const ChatPage = lazy(() =>
  import("./containers/ChatPage/ChatPage").then((m) => ({
    default: m.ChatPage,
  })),
);

export default function Routes() {
  return (
    <Router>
      {/* Single Suspense boundary for every lazy marketplace route. Blog
          keeps its own inner `fallback={null}` (nearest boundary wins), so
          its behaviour is unchanged. */}
      <Suspense fallback={<FullPageLoading />}>
      <Switch>
        {/* Flat marketplace routes. No country/language prefix — country is
            a per-post tag (rendered as a flag on each card) and language is
            a cookie/profile preference, not a URL segment. */}
        <Route exact path="/" component={HomePage} />
        <Route exact path="/post/:postId" component={PostDetailPage} />
        <Route exact path="/settings" component={SettingsPage} />
        <Route exact path="/privacy" component={PrivacyPage} />
        <Route exact path="/terms" component={TermsPage} />
        <Route exact path="/user/:userId" component={OtherProfilePage} />
        <Route
          exact
          path="/edit/:postId"
          component={() => (
            <PrivateRoute>
              <AddPostPage isEdit />
            </PrivateRoute>
          )}
        />
        {/* Anonymous users can post — AddPostPage shows a guest/sign-in
            modal at submit time. /edit/:postId stays PrivateRoute since
            only the post's owner can edit. */}
        <Route exact path="/add-post" component={AddPostPage} />
        {/* My Posts works for anon too — the page reads the user id from
            the `uid` cookie, which is set for both anon and authed users. */}
        <Route exact path="/posts" component={MyPostsPage} />
        <Route exact path="/posts/:userId" component={OthersPostsPage} />
        {/* Profile is reachable by guests — ProfilePage shows a "sign in to
            create your profile" prompt when there's no signed-in user, instead
            of bouncing home + force-opening the auth wizard. /profile/edit
            below stays gated. */}
        <Route exact path="/profile" component={ProfilePage} />
        <Route
          exact
          path="/profile/edit"
          component={() => (
            <PrivateRoute>
              <ProfilePageEdit />
            </PrivateRoute>
          )}
        />
        <Route exact path="/chats" component={ChatPage} />
        <Route exact path="/chats/:chatId" component={ChatPage} />

        {/* Traffic-driver surface — lazy-loaded so it doesn't bloat the
            marketplace's initial bundle. Suspense fallback is intentionally
            blank: the loaded chunk is tiny and the marketplace header from
            AppLayout renders inside the lazy page, so a loader here would
            just add visual noise.

            dresez.com no longer hosts its own games: /games and
            /games/<slug> are 301'd to https://games.dresez.com/ by NGINX
            (devops/nginx/conf.d/dresez.com.conf), so those paths never
            reach the router in production. */}
        <Route exact path="/blog">
          <Suspense fallback={null}>
            <BlogListPage />
          </Suspense>
        </Route>
        <Route exact path="/blog/:slug">
          <Suspense fallback={null}>
            <BlogPostPage />
          </Suspense>
        </Route>

        {/* Legacy auth routes — /signin, /signin/email, /signin/email/reset,
            /signup all collapse to "redirect home + open the AuthWizard".
            The wizard handles both first-signup and returning-user signin. */}
        <Route path={["/signin", "/signup"]} component={AuthWizardRedirect} />

        {/* Legacy redirect — preserves shared/cached `/us/en/post/123` style
            URLs by stripping the first two path segments and 302-ing to the
            flat equivalent. `path` not exact so `/us/en` AND `/us/en/post/x`
            both match. */}
        <Route path="/:country/:language" component={LegacyCountryLangRedirect} />

        {/* Final fallback for anything else. */}
        <Route component={() => <Redirect to="/" />} />
      </Switch>
      </Suspense>
    </Router>
  );
}

function AuthWizardRedirect() {
  // Anything matching /signin* or /signup* — open the modal and bounce home.
  // useEffect (not inline) so React Router has a chance to render before
  // we trigger the singleton; otherwise the Modal mounts in a "no host yet"
  // moment.
  useEffect(() => {
    openAuthWizard();
  }, []);
  return <Redirect to="/" />;
}

function LegacyCountryLangRedirect() {
  const { pathname, search, hash } = useLocation();
  // Strip the first two path segments (the legacy country + language).
  // /us/en              → /
  // /us/en/post/abc     → /post/abc
  const stripped = pathname.replace(/^\/[^/]+\/[^/]+/, "") || "/";
  return <Redirect to={{ pathname: stripped, search, hash }} />;
}

function PrivateRoute(props) {
  const history = useHistory();
  const { user, isLoading } = useSelector((state) => state.user);

  useEffect(() => {
    if (!user?.id && !isLoading) {
      // Redirect home + pop the AuthWizard. Previously this pushed to
      // /signin which is gone now.
      history.replace("/");
      openAuthWizard();
    }
  }, []);

  if (user?.id) {
    return props.children;
  }

  return null;
}
